import React, { useEffect, useState } from 'react';
import { Grid, Paper, OutlinedInput, InputLabel, Button, Box, Typography, IconButton, Collapse, LinearProgress } from '@mui/material';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { dataGridLocaleConfig } from '../../../config/data-grid-locale';
import { useSupplierSearchController } from './context/supplier-search-controller';
import { useSupplierSearchState } from './context/supplier-search-context';
import SupplierAppService from '../../../application/platform/sge/supplier/supplier-app-service';
import { ISupplierPagedResponse } from '../../../model/sge/supplier/response/supplier-paged-response';
import { formatDocument } from '../../../utils/document';

export function SupplierSearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [rows, setRows] = useState<ISupplierPagedResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const { handleOnChange, handleSearch, handleClearFilters, navigateToCreate, navigateToEdit } = useSupplierSearchController();
    const { parameters, searchTrigger } = useSupplierSearchState();

    useEffect(() => {
        setLoading(true);
        SupplierAppService.list(parameters).then((r) => setRows(r.items)).finally(() => setLoading(false));
    }, [searchTrigger]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80, hide: true },
        { field: 'personName', headerName: 'Nome', flex: 1, minWidth: 200 },
        {
            field: 'documento', headerName: 'Documento', width: 160,
            valueGetter: (params) => formatDocument(params.row.cpf || params.row.cnpj)
        },
        { field: 'isActive', headerName: 'Ativo', width: 80, type: 'boolean' },
        {
            field: 'actions', headerName: 'Ações', width: 80, sortable: false,
            renderCell: (params) => <IconButton size="small" onClick={() => navigateToEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader title="Fornecedores" subtitle="Gerencie os fornecedores cadastrados" breadcrumbs={[{ label: 'Fornecedores' }]} />
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 1, p: 1, ml: -1, mr: -1, mb: filtersOpen ? 2 : 0, '&:hover': { backgroundColor: 'action.hover' } }} onClick={() => setFiltersOpen(!filtersOpen)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FilterListIcon color="action" /><Typography variant="h6">Filtros</Typography></Box>
                        <IconButton size="small" sx={{ pointerEvents: 'none' }}>{filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                    </Box>
                    <Collapse in={filtersOpen}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <InputLabel>Buscar por nome ou documento</InputLabel>
                                <OutlinedInput fullWidth name="input-term" value={parameters.term || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={handleClearFilters}>Limpar</Button>
                                    <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Consultar</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ position: 'relative', pb: '28px' }}>
                    <Paper sx={{ p: 2, minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            pageSize={100}
                            sx={{ flex: 1 }}
                            disableSelectionOnClick
                            experimentalFeatures={{ newEditingApi: true }}
                            components={{ LoadingOverlay: LinearProgress }}
                            localeText={dataGridLocaleConfig}
                        />
                    </Paper>
                    <SpeedDial ariaLabel="Novo Fornecedor" sx={{ position: 'absolute', bottom: 0, right: 16 }} onClick={navigateToCreate} icon={<SpeedDialIcon />} />
                </Box>
            </Grid>
        </React.Fragment>
    );
}
